<script>
import moment from 'moment';
import DoughnutChart from '@components/charts/doughnut-chart.vue';

import { trainerComputed, homeMethods, homeComputed, orgComputed, orgMethods } from '@state/helpers';

export default {
  name: 'CalendarHome',
  components: {
    DoughnutChart,
  },
  props: {
    currentWeek: {
      type: Object,
      required: true,
      default: () => {},
    },
    workoutOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    greeting: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      todaysDate: moment().format('MMM Do'),
      doughnutChartHeight: '0px',
      doughnutChartWidth: '0px',
      emptyProgram: false,
      placeholder: require('@assets/images/placeholder-avatar.png'),
      chartData: {
        datasets: [
          {
            backgroundColor: [process.env.VUE_APP_ORG_ID === '618183d65e84466f63c38878' ? '#FF7F50' : '#4059E1' , 'white'],
            borderWidth: 0,
            hoverBorderColor: false,
          },
        ],
      },
      chartOpts: {
        cutoutPercentage: 80,
        tooltips: {
          enabled: false,
        },
        legend: {
          display: false,
        },
        hover: false,
      },
    };
  },
  mounted() {
    window.addEventListener('resize', this.getDoughnutChartDim);

    // Auto select the current day on mount for the current day
    if (this.currentWeek && this.currentWeek.days.length > 0) {
      this.currentWeek.days.forEach((day) => {
        const dayDate = moment(day.date);
        if (day && moment.utc().isSame(dayDate, 'day')) {
          this.setSelectedDay(day);
        }
      });
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.getDoughnutChartDim);
  },
  computed: {
    ...trainerComputed,
    ...homeComputed,
    ...orgComputed,
    colors() {
      return {
        '--color':  this.primaryColor
      }
    },
    trainerAvatar() {
      return this.myTrainer &&
        this.myTrainer.mediaAssets &&
        this.myTrainer.mediaAssets.avatarImage &&
        this.myTrainer.mediaAssets.avatarImage.url
        ? this.myTrainer.mediaAssets.avatarImage.url
        : this.placeholder;
    },
    isProgramEmpty() {
      return this.homeOverview && this.homeOverview.isProgramEmpty;
    },
  },
  methods: {
    ...homeMethods,
    ...orgMethods,
    getDoughnutChartDim() {
      const canvas = document.getElementById('doughnut-chart');
      if (canvas) {
        this.doughnutChartHeight = canvas.clientHeight - 5 + 'px';
        this.doughnutChartWidth = canvas.clientWidth - 5 + 'px';
      }
    },
    formatDayDate(date) {
      if (date) {
        return moment.utc(date).format('D');
      }
    },
    formatDayName(date) {
      if (date) {
        return moment.utc(date).format('dd');
      }
    },
    isToday(date, day) {
      const dayDate = moment(date);
      return moment.utc().isSame(dayDate, 'day');
    },
    isWorkoutCompleted(day) {
      return (
        day &&
        day.workouts &&
        day.workouts.length > 0 &&
        day.workouts[0].hasWorkout &&
        day.workouts[0].clientInput.completedAt &&
        day.workouts[0].clientInput.completedAt !== ''
      );
    },
    workoutProgress(day) {
      const totalWorkoutElements =
        day &&
        day.workouts &&
        day.workouts.length > 0 &&
        day.workouts[0].hasWorkout &&
        day.workouts[0].clientInput.workoutProgress &&
        day.workouts[0].clientInput.workoutProgress.totalWorkoutElements
          ? day.workouts[0].clientInput.workoutProgress.totalWorkoutElements
          : 0;
      const completedWorkoutElements =
        day &&
        day.workouts &&
        day.workouts.length > 0 &&
        day.workouts[0].hasWorkout &&
        day.workouts[0].clientInput.workoutProgress &&
        day.workouts[0].clientInput.workoutProgress.completedWorkoutElements
          ? day.workouts[0].clientInput.workoutProgress.completedWorkoutElements
          : 0;

      return this.isWorkoutCompleted(day)
        ? 100
        : totalWorkoutElements > 0
        ? (completedWorkoutElements / totalWorkoutElements) * 100
        : 0;
    },
  },
};
</script>

<template>
  <div class="card">
    <div class="row item">
      <b-col md="12" sm="12">
        <div class="date"
          ><span>{{ todaysDate }}</span
          ><div v-if="homeOverview && homeOverview.checkInNeedingClientInput" @click="$bvModal.show('checkin-modal')"
            ><svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M14.875 0.125H2.125C0.929688 0.125 0 1.08789 0 2.25V11.8125C0 13.0078 0.929688 13.9375 2.125 13.9375H5.3125V16.7266C5.3125 17.0586 5.67773 17.2578 5.94336 17.0586L10.0938 13.9375H14.875C16.0371 13.9375 17 13.0078 17 11.8125V2.25C17 1.08789 16.0371 0.125 14.875 0.125ZM8.5 11.2812C7.90234 11.2812 7.4375 10.8164 7.4375 10.2188C7.4375 9.6543 7.90234 9.15625 8.5 9.15625C9.06445 9.15625 9.5625 9.6543 9.5625 10.2188C9.5625 10.8164 9.06445 11.2812 8.5 11.2812ZM9.33008 7.62891C9.29688 7.89453 9.06445 8.09375 8.79883 8.09375H8.16797C7.90234 8.09375 7.66992 7.89453 7.63672 7.62891L7.20508 3.37891C7.17188 3.08008 7.4375 2.78125 7.73633 2.78125H9.23047C9.5293 2.78125 9.79492 3.08008 9.76172 3.37891L9.33008 7.62891Z"
                fill="#4059E1"
              /></svg
            >Complete Your Check-In</div
          ></div
        >
      </b-col>
      <b-col md="12" sm="12">
        <p v-if="!homeOverview.todaysTrainerNote && !isProgramEmpty" class="greeting">{{ greeting }}</p>
      </b-col>
      <div class="row upper-section">
        <div class="col-4 avatar-wrapper">
          <img v-if="(homeOverview && homeOverview.todaysTrainerNote) || isProgramEmpty" :src="trainerAvatar" />
        </div>
        <div class="col note-wrapper">
          <div class="note">
            <p v-if="homeOverview && homeOverview.todaysTrainerNote && !isProgramEmpty">{{ homeOverview.todaysTrainerNote }}</p>
            <p v-if="isProgramEmpty"
              >I’m just finishing up your program! I will hit you up when I have it uploaded and you’re ready for day 1!</p
            >
          </div>
        </div>
      </div>
      <b-col v-if="!isProgramEmpty" md="12" sm="12">
        <hr />
      </b-col>
      <div v-if="isProgramEmpty" class="row say-hello-wrapper">
        <BaseButton @click="setViewChatList(!viewChatList)" class="button btn-primary large">
          <p><BaseIcon class="icon-comment-dots" name="comment-dots" prefix="far" />Say Hello</p>
        </BaseButton>
      </div>
      <b-col v-if="!isProgramEmpty" md="12" sm="12">
        <div class="wekend">
          <template v-for="day in currentWeek.days">
            <div :key="day.date" :class="['day']" @click="setSelectedDay(day)">
              <p>{{ formatDayName(day.date) }}</p>
              <div class="doughnut-chart-wrapper">
                <div class="doughnut-chart-data">
                  <div
                    :class="[
                      'chart-num-wrapper',
                      { isToday: isToday(day.date, day), selected: selectedDay && selectedDay.date === day.date },
                    ]"
                    :style="{ width: doughnutChartWidth, height: doughnutChartHeight }"
                  >
                    <p>{{ formatDayDate(day.date) }}</p>
                    <div v-if="isWorkoutCompleted(day)" class="icon-check-wrapper">
                      <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-check'" :style="colors" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
                    </div>
                  </div>
                  <DoughnutChart
                    @update-chart-dim="getDoughnutChartDim"
                    class="doughnut-chart"
                    :data="{
                      ...chartData,
                      datasets: [
                        {
                          ...chartData.datasets[0],
                          data: [workoutProgress(day), 100 - workoutProgress(day)],
                        },
                      ],
                    }"
                    :options="chartOpts"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
      </b-col>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.note {
  align-items: center;
  color: #000;
  display: flex;
  font-family: $secondary-font-family;
  font-size: 1rem;
  font-style: normal;
  font-weight: normal;
  line-height: 30px;
  margin: 0 18px 0 0;
  max-height: 11rem;
  min-height: 11rem;
  mix-blend-mode: normal;
  text-align: center;
  text-align: left;
  p {
    @include calc-font-size(1.2);

    font-weight: normal;
    line-height: 30px;
  }
}

.doughnut-chart-wrapper {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-around;
  margin-right: 2rem;
  transition: all 200ms;
  transition-timing-function: ease-out;
  width: 100%;
  &:hover {
    .chart-num-wrapper {
      background-color: rgba(black, 0.1);
    }

    transform: scale(1.08);
  }

  .doughnut-chart-data {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    position: relative;
    width: 100%;
    .doughnut-chart {
      height: fit-content;
      max-height: 6rem;
      max-width: 6rem;
      position: absolute;
      width: 70%;
      .chartjs-size-monitor {
        height: fit-content;
      }
    }

    .chart-num-wrapper {
      align-items: center;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      position: relative;
      text-align: center;
      p {
        color: $color-heading-text;
        font-family: $secondary-font-family !important;
        font-size: calc(2.1rem + (21 - 16) * ((100vw - 30rem) / (1600 - 300))) !important;
        font-weight: 300 !important;
        margin-bottom: unset !important;
      }
      &.isToday {
        background-color: #3f3f3f;
        p {
          color: #fff !important;
          font-family: $secondary-font-family !important;
          font-weight: bold !important;
        }
      }
      &.selected {
        &:not(.isToday) {
          background-color: rgba(black, 0.1);
        }
      }
    }
  }

  label {
    margin-top: 35%;
    white-space: nowrap;
  }
}

.card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 18px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 30rem;
  position: relative;
  width: 100%;
  .item {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    padding: 3% 5% 2.5% 5%;
    position: relative;
    .upper-section {
      width: 100%;
    }
    .note-wrapper {
      align-items: center;
      display: flex;
    }
    .avatar-wrapper {
      align-items: center;
      display: flex;
      margin-left: 2rem;
      max-width: fit-content;
      img {
        border-radius: 50%;
        height: 9rem;
        object-fit: cover;
        width: 9rem;
      }
    }

    .date {
      align-items: center;
      display: flex;
      justify-content: space-between;
      span {
        align-items: center;
        color: #000;
        display: flex;
        font-family: $secondary-font-family;
        font-size: 30px;
        font-style: normal;
        font-weight: 600;
        line-height: 30px;
        padding-bottom: 22px;
        text-transform: capitalize;
      }
      div {
        color: #4059e1;
        cursor: pointer;
        float: right;
        font-size: 17px;
        font-style: normal;
        font-weight: bold;
        line-height: 23px;
        padding-bottom: 22px;
        text-align: center;
        svg {
          margin-right: 7px;
        }
      }
    }
    .greeting {
      color: #3f3f3f;
      font-family: $secondary-font-family;
      font-size: 1.8rem;
      font-style: normal;
      font-weight: 300;
      line-height: 18px;
      mix-blend-mode: normal;
    }
    .say-hello-wrapper {
      margin-bottom: 2rem;
      margin-right: unset;
      margin-top: 1rem;
      position: relative;
      white-space: nowrap;
      width: 100%;

      .button {
        align-items: center;
        display: flex;
        height: 6vw;
        justify-content: center;
        margin-left: 15rem;
        max-height: 6rem;
        padding: 1vw 2vw;
        right: 7%;
        white-space: nowrap;
        width: fit-content;
        svg {
          border-width: 1px;
          height: 2.7rem;
          margin-right: 7px;
          width: 2.7rem;
          path {
            border-width: 1px;
          }
        }
        p {
          align-items: center;
          color: #fff;
          display: flex;
          font-style: normal;
          font-weight: 500;
          justify-content: center;
          margin-bottom: unset;
          text-align: center;
          text-transform: capitalize;
          white-space: nowrap;
          @include calc-font-size(1.2);
        }
      }
    }

    .wekend {
      display: flex;
      height: 7vw;
      justify-content: space-evenly;
      .day {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-evenly;
        position: relative;
        width: 100%;
        p {
          color: #000;
          font-family: $secondary-font-family;
          font-style: normal;
          font-weight: normal;
          line-height: 30px;
          margin-bottom: 0.5rem;
          text-align: center;
          @include calc-font-size(1.8);
        }
        div {
          font-family: $secondary-font-family;
          font-size: 1.7rem;
          font-weight: 100;
          line-height: 30px;
          opacity: 1;
        }

        .icon-check-wrapper {
          align-items: center;
          border: 4px solid #1c1c1c;
          border-radius: 100%;
          bottom: -20%;
          display: flex;
          height: 50%;
          justify-content: center;
          position: absolute;
          right: -10%;
          text-align: center;
          width: 50%;
          z-index: 1;

          .icon-check {
            background-color: var(--color);
            border-radius: 100%;
            height: 100%;
            width: 100%;
            path {
              &:first-child {
                background-color:  var(--color);
                fill: var(--color);
                fill-opacity: 1;
                stroke: var(--color);
              }

              opacity: 1 !important;
            }
          }
        }
        &.completed {
          p {
            margin-bottom: unset;
          }
        }
      }
    }
  }
}
</style>
